/**
 * Subscription Service Layer
 *
 * Provides functions to manage teacher subscriptions.
 * - 14-day free trial on signup
 * - $30/year subscription via Stripe
 * - Auto-renewal handling
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  getDocs,
  collection,
  query,
  where,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './config';

// ============================================================================
// Types
// ============================================================================

export type SubscriptionStatus = 'trial' | 'active' | 'expired' | 'canceled';

export interface Subscription {
  status: SubscriptionStatus;

  // Trial tracking (may not exist if created via webhook)
  trialStartDate?: Date;
  trialEndsAt?: Date;

  // Paid subscription tracking
  startDate?: Date;
  expiresAt?: Date;

  // Stripe integration
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;

  // Notification tracking (used by Cloud Functions)
  notificationsSent?: {
    day7?: Date;
    day3?: Date;
    day1?: Date;
  };

  createdAt?: Date;
  updatedAt?: Date;
}

interface FirestoreSubscription {
  status: SubscriptionStatus;
  // Trial fields (may not exist for webhook-created subscriptions)
  trialStartDate?: Timestamp;
  trialEndsAt?: Timestamp;
  // Main dates
  startDate?: Timestamp;
  expiresAt?: Timestamp;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  // Notification tracking
  notificationsSent?: {
    day7?: Timestamp;
    day3?: Timestamp;
    day1?: Timestamp;
  };
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// Firebase Stripe Extension subscription format
interface StripeExtensionSubscription {
  status: string; // 'active' | 'trialing' | 'incomplete' | 'incomplete_expired' | 'past_due' | 'canceled' | 'unpaid'
  current_period_start: Timestamp;
  current_period_end: Timestamp;
  cancel_at_period_end?: boolean;
  canceled_at?: Timestamp;
  created: Timestamp;
  trial_start?: Timestamp;
  trial_end?: Timestamp;
  // Additional Stripe fields we might use
  customer?: string;
  items?: Array<{ price: { id: string } }>;
}

// ============================================================================
// Constants
// ============================================================================

const TRIAL_DAYS = 14;
const SUBSCRIPTION_DAYS = 365;

// ============================================================================
// Helpers
// ============================================================================

function toSubscription(data: FirestoreSubscription): Subscription {
  return {
    status: data.status,
    trialStartDate: data.trialStartDate?.toDate(),
    trialEndsAt: data.trialEndsAt?.toDate(),
    startDate: data.startDate?.toDate(),
    expiresAt: data.expiresAt?.toDate(),
    stripeCustomerId: data.stripeCustomerId,
    stripeSubscriptionId: data.stripeSubscriptionId,
    notificationsSent: data.notificationsSent
      ? {
          day7: data.notificationsSent.day7?.toDate(),
          day3: data.notificationsSent.day3?.toDate(),
          day1: data.notificationsSent.day1?.toDate(),
        }
      : undefined,
    createdAt: data.createdAt?.toDate(),
    updatedAt: data.updatedAt?.toDate(),
  };
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function isExpired(date: Date): boolean {
  return date < new Date();
}

/**
 * Maps Stripe extension subscription status to our app's SubscriptionStatus
 */
function mapStripeStatus(stripeStatus: string): SubscriptionStatus {
  switch (stripeStatus) {
    case 'trialing':
      return 'trial';
    case 'active':
      return 'active';
    case 'canceled':
    case 'unpaid':
      return 'canceled';
    case 'incomplete':
    case 'incomplete_expired':
    case 'past_due':
    default:
      return 'expired';
  }
}

/**
 * Converts a Stripe extension subscription to our Subscription format
 * @param data The subscription data from Firestore
 * @param subscriptionId The Stripe subscription ID
 * @param stripeCustomerId Optional Stripe customer ID (from parent customers doc)
 */
function stripeToSubscription(
  data: StripeExtensionSubscription,
  subscriptionId: string,
  stripeCustomerId?: string
): Subscription {
  const status = mapStripeStatus(data.status);

  return {
    status,
    // Map trial dates if in trial
    trialStartDate: data.trial_start?.toDate(),
    trialEndsAt: data.trial_end?.toDate(),
    // Map subscription dates
    startDate: data.current_period_start?.toDate(),
    expiresAt: data.current_period_end?.toDate(),
    // Stripe IDs - prefer passed stripeCustomerId, fall back to data.customer
    stripeCustomerId: stripeCustomerId || data.customer,
    stripeSubscriptionId: subscriptionId,
    // Created/updated timestamps
    createdAt: data.created?.toDate(),
    updatedAt: data.created?.toDate(),
  };
}

// ============================================================================
// Subscription Service
// ============================================================================

export const subscriptionsService = {
  /**
   * Get a teacher's subscription.
   * First checks the Firebase Stripe Extension collection, then falls back to legacy collection.
   */
  async get(teacherId: string): Promise<Subscription | null> {
    // First, try to get from Firebase Stripe Extension collection
    const extensionSubsRef = collection(db, 'customers', teacherId, 'subscriptions');
    const extensionSubsSnap = await getDocs(extensionSubsRef);

    if (!extensionSubsSnap.empty) {
      // Find the most relevant subscription (active or trialing preferred)
      const activeSub = extensionSubsSnap.docs.find(doc => {
        const status = doc.data().status;
        return status === 'active' || status === 'trialing';
      });

      const subDoc = activeSub || extensionSubsSnap.docs[0];

      // Get the stripeId from the parent customers document
      const customerRef = doc(db, 'customers', teacherId);
      const customerDoc = await getDoc(customerRef);
      const stripeCustomerId = customerDoc.exists() ? customerDoc.data()?.stripeId : undefined;

      return stripeToSubscription(
        subDoc.data() as StripeExtensionSubscription,
        subDoc.id,
        stripeCustomerId
      );
    }

    // Fall back to legacy subscriptions collection for backwards compatibility
    const legacySubRef = doc(db, 'subscriptions', teacherId);
    const legacySubDoc = await getDoc(legacySubRef);

    if (!legacySubDoc.exists()) {
      return null;
    }

    return toSubscription(legacySubDoc.data() as FirestoreSubscription);
  },

  /**
   * Create a new trial subscription for a teacher.
   * Called when a teacher signs up.
   */
  async createTrial(teacherId: string): Promise<Subscription> {
    const subRef = doc(db, 'subscriptions', teacherId);

    // Check if subscription already exists
    const existing = await getDoc(subRef);
    if (existing.exists()) {
      return toSubscription(existing.data() as FirestoreSubscription);
    }

    const now = new Date();
    const trialEndsAt = addDays(now, TRIAL_DAYS);

    const subscriptionData: Omit<FirestoreSubscription, 'createdAt' | 'updatedAt'> & {
      createdAt: ReturnType<typeof serverTimestamp>;
      updatedAt: ReturnType<typeof serverTimestamp>;
    } = {
      status: 'trial',
      trialStartDate: Timestamp.fromDate(now),
      trialEndsAt: Timestamp.fromDate(trialEndsAt),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(subRef, subscriptionData);

    return {
      status: 'trial',
      trialStartDate: now,
      trialEndsAt: trialEndsAt,
      createdAt: now,
      updatedAt: now,
    };
  },

  /**
   * Activate a paid subscription after successful Stripe payment.
   */
  async activate(
    teacherId: string,
    stripeCustomerId: string,
    stripeSubscriptionId: string
  ): Promise<void> {
    const subRef = doc(db, 'subscriptions', teacherId);
    const now = new Date();
    const expiresAt = addDays(now, SUBSCRIPTION_DAYS);

    await updateDoc(subRef, {
      status: 'active',
      startDate: Timestamp.fromDate(now),
      expiresAt: Timestamp.fromDate(expiresAt),
      stripeCustomerId,
      stripeSubscriptionId,
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Extend subscription by one year (for renewals).
   */
  async extend(teacherId: string): Promise<void> {
    const subRef = doc(db, 'subscriptions', teacherId);
    const subDoc = await getDoc(subRef);

    if (!subDoc.exists()) {
      throw new Error('Subscription not found');
    }

    const data = subDoc.data() as FirestoreSubscription;
    const currentExpiry = data.expiresAt?.toDate() || new Date();
    const newExpiry = addDays(currentExpiry, SUBSCRIPTION_DAYS);

    await updateDoc(subRef, {
      status: 'active',
      expiresAt: Timestamp.fromDate(newExpiry),
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Mark subscription as expired.
   */
  async markExpired(teacherId: string): Promise<void> {
    const subRef = doc(db, 'subscriptions', teacherId);

    await updateDoc(subRef, {
      status: 'expired',
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Mark subscription as canceled.
   */
  async markCanceled(teacherId: string): Promise<void> {
    const subRef = doc(db, 'subscriptions', teacherId);

    await updateDoc(subRef, {
      status: 'canceled',
      updatedAt: serverTimestamp(),
    });
  },

  /**
   * Check if subscription has valid access.
   * Returns access info including whether it's valid and days remaining.
   */
  checkAccess(subscription: Subscription | null): {
    hasAccess: boolean;
    status: SubscriptionStatus | 'none';
    daysRemaining: number;
    isTrialEnding: boolean; // true if trial ends within 3 days
    isExpiringSoon: boolean; // true if <= 7 days remaining
    expirationDate: Date | null;
    canAccessPortal: boolean; // true if stripeCustomerId exists
  } {
    if (!subscription) {
      return {
        hasAccess: false,
        status: 'none',
        daysRemaining: 0,
        isTrialEnding: false,
        isExpiringSoon: false,
        expirationDate: null,
        canAccessPortal: false,
      };
    }

    const now = new Date();
    const canAccessPortal = !!subscription.stripeCustomerId;

    if (subscription.status === 'trial') {
      // Trial subscriptions may use trialEndsAt or expiresAt depending on how they were created
      const endDate = subscription.trialEndsAt || subscription.expiresAt;
      if (!endDate) {
        return {
          hasAccess: false,
          status: 'expired',
          daysRemaining: 0,
          isTrialEnding: false,
          isExpiringSoon: false,
          expirationDate: null,
          canAccessPortal,
        };
      }

      const hasAccess = !isExpired(endDate);
      const daysRemaining = Math.max(
        0,
        Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      );

      return {
        hasAccess,
        status: hasAccess ? 'trial' : 'expired',
        daysRemaining,
        isTrialEnding: daysRemaining <= 3 && daysRemaining > 0,
        isExpiringSoon: daysRemaining <= 7 && daysRemaining > 0,
        expirationDate: endDate,
        canAccessPortal,
      };
    }

    if (subscription.status === 'active' && subscription.expiresAt) {
      const hasAccess = !isExpired(subscription.expiresAt);
      const daysRemaining = Math.max(
        0,
        Math.ceil((subscription.expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
      );

      return {
        hasAccess,
        status: hasAccess ? 'active' : 'expired',
        daysRemaining,
        isTrialEnding: false,
        isExpiringSoon: daysRemaining <= 7 && daysRemaining > 0,
        expirationDate: subscription.expiresAt,
        canAccessPortal,
      };
    }

    return {
      hasAccess: false,
      status: subscription.status,
      daysRemaining: 0,
      isTrialEnding: false,
      isExpiringSoon: false,
      expirationDate: subscription.expiresAt || null,
      canAccessPortal,
    };
  },

  /**
   * Subscribe to real-time subscription updates.
   * Listens to the Firebase Stripe Extension collection first.
   */
  subscribe(teacherId: string, callback: (sub: Subscription | null) => void): Unsubscribe {
    // Listen to Firebase Stripe Extension subscriptions collection
    const extensionSubsRef = collection(db, 'customers', teacherId, 'subscriptions');
    const customerRef = doc(db, 'customers', teacherId);

    // Query for active/trialing subscriptions
    const extensionQuery = query(
      extensionSubsRef,
      where('status', 'in', ['active', 'trialing'])
    );

    let extensionUnsubscribe: Unsubscribe | null = null;
    let customerUnsubscribe: Unsubscribe | null = null;
    let legacyUnsubscribe: Unsubscribe | null = null;
    let hasExtensionSub = false;
    let cachedStripeCustomerId: string | undefined;
    let cachedSubDoc: { data: StripeExtensionSubscription; id: string } | null = null;

    // Helper to emit subscription with customer ID
    const emitSubscription = () => {
      if (cachedSubDoc) {
        callback(
          stripeToSubscription(
            cachedSubDoc.data,
            cachedSubDoc.id,
            cachedStripeCustomerId
          )
        );
      }
    };

    // Listen to customer document for stripeId changes
    customerUnsubscribe = onSnapshot(customerRef, (customerDoc) => {
      if (customerDoc.exists()) {
        cachedStripeCustomerId = customerDoc.data()?.stripeId;
        // Re-emit if we have a subscription
        if (hasExtensionSub && cachedSubDoc) {
          emitSubscription();
        }
      }
    });

    // Listen to extension collection
    extensionUnsubscribe = onSnapshot(extensionQuery, (snapshot) => {
      if (!snapshot.empty) {
        hasExtensionSub = true;
        const subDoc = snapshot.docs[0];
        cachedSubDoc = {
          data: subDoc.data() as StripeExtensionSubscription,
          id: subDoc.id
        };
        emitSubscription();
      } else {
        hasExtensionSub = false;
        cachedSubDoc = null;
        // If no extension subscription, try legacy collection
        if (!legacyUnsubscribe) {
          const legacySubRef = doc(db, 'subscriptions', teacherId);
          legacyUnsubscribe = onSnapshot(legacySubRef, (legacyDoc) => {
            // Only use legacy if extension doesn't have a subscription
            if (!hasExtensionSub) {
              if (!legacyDoc.exists()) {
                callback(null);
                return;
              }
              callback(toSubscription(legacyDoc.data() as FirestoreSubscription));
            }
          });
        }
      }
    });

    // Return a combined unsubscribe function
    return () => {
      if (extensionUnsubscribe) extensionUnsubscribe();
      if (customerUnsubscribe) customerUnsubscribe();
      if (legacyUnsubscribe) legacyUnsubscribe();
    };
  },
};
